import { useCallback, useEffect, useMemo, useState } from "react";

import { getOrganizationConfigByDomain } from "../../../shared/utils/Global";
import { Navbar } from "./Navbar";
import {
  buildNavbarConfigPayload,
  DASHBOARD_NAVBAR_CONFIG,
  resolveNavbarUser,
  resolveProgrammeType,
  resolveUniqueId,
  type ConfigNavbarUserState,
  type DashboardNavbarVariant
} from "./dashboardNavbarConfig";
import { mapUiLayoutToNavigationConfig } from "./mapUiLayoutToNavigationConfig";
import type { LayoutNavigationResponse } from "./mapUiLayoutToNavigationConfig";
import type { NavigationConfig } from "./types";

export interface ConfigNavbarWrapperProps {
  variant: DashboardNavbarVariant;
  userState: ConfigNavbarUserState;
  logout: () => void;
  resetIdentity: () => void;
  getToken: () => string;
  postRequest: (
    endpoint: string,
    token: string,
    body: Record<string, unknown>
  ) => Promise<{ status?: boolean } | null | undefined>;
}

export function ConfigNavbarWrapper({
  variant,
  userState,
  logout,
  resetIdentity,
  getToken,
  postRequest
}: ConfigNavbarWrapperProps) {
  const dashboardConfig = DASHBOARD_NAVBAR_CONFIG[variant];
  const [apiConfig, setApiConfig] = useState<NavigationConfig | null>(null);
  const isAuthenticated = userState.isAuthenticated === true;

  const executeAction = useCallback(
    (key: string) => {
      if (key === "logout") {
        logout();
        resetIdentity();
      }
    },
    [logout, resetIdentity]
  );

  useEffect(() => {
    const orgConfig = getOrganizationConfigByDomain();

    console.log(orgConfig, userState);

    const uniqueId = resolveUniqueId(
      variant,
      { ...userState, isAuthenticated },
      orgConfig
    );
    const programmeType = resolveProgrammeType(
      variant,
      { ...userState, isAuthenticated },
      dashboardConfig,
      orgConfig
    );

    console.log(uniqueId, programmeType);

    if (!uniqueId || !programmeType) return;

    const fetchNavbarConfig = async () => {
      try {
        const token = isAuthenticated ? getToken() : "";
        const { apiPrefix, service } = dashboardConfig;
        const endpoint = isAuthenticated
          ? `/${apiPrefix}/fetch-uiconfig`
          : `/${apiPrefix}/fetch-login-config`;
        const resourceName = isAuthenticated ? "layout" : "login";

        const response = await postRequest(
          endpoint,
          token,
          buildNavbarConfigPayload(
            uniqueId,
            programmeType,
            resourceName,
            service
          )
        );

        if (!response?.status) return;

        const mappedConfig = mapUiLayoutToNavigationConfig(
          response as LayoutNavigationResponse
        );
        if (mappedConfig) {
          setApiConfig(mappedConfig);
        }
      } catch (error) {
        console.error("Unable to fetch navbar ui config", error);
      }
    };

    fetchNavbarConfig();
  }, [
    dashboardConfig,
    getToken,
    isAuthenticated,
    postRequest,
    userState.organizationID,
    userState.userProgramTypes,
    userState.userData?.organizationID,
    userState.userData?.lenderID,
    userState.userData?.userProgramTypes,
    variant
  ]);

  const resolvedConfig = useMemo(() => {
    if (!apiConfig) return null;

    if (isAuthenticated) {
      return apiConfig;
    }

    return { ...apiConfig, profileMenu: undefined };
  }, [apiConfig, isAuthenticated]);

  if (!resolvedConfig) {
    return null;
  }

  return (
    <Navbar
      config={resolvedConfig}
      user={resolveNavbarUser(variant, { ...userState, isAuthenticated })}
      executeAction={executeAction}
    />
  );
}

export default ConfigNavbarWrapper;
